import React, { useEffect, useState } from 'react';
import { storyBibleApi } from '../../api/storyBible';
import { categoryDisplayName, TIER1_CATEGORIES, TIER2_CATEGORIES } from '../BookSheet/categories';
import { categoryForAddedNote, isSectionHeading, UNSORTED_NOTE_CATEGORY } from '../../utils/noteCategory';
import { ADD_A_NOTE, ADD_TO_BOOK_NOTES } from '../../utils/writerCopy';
import { segmentMessageInputTokens } from '../../utils/estimatePaidActionCost';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import './AddToBibleModal.css';

const NOTE_CATEGORIES = [...TIER1_CATEGORIES, ...TIER2_CATEGORIES, UNSORTED_NOTE_CATEGORY];

let _cardKey = 0;
const nextKey = () => `card-${_cardKey++}`;

/**
 * AddToBibleModal — the "Add to Bible" review sheet.
 *
 * On open it asks before a paid read, then segments the ONE selected message into atomic
 * proposed cards (segment-don't-synthesize; one idea per card; under-fill rather
 * than invent). Each proposed card is editable, splittable, and discardable.
 *
 * Approving IS the confirm-to-canon step — there is no second "make canon" click.
 * Approved cards persist to story_bible_entries via the manual path (confirmed=
 * true, source_type='manual', source_ref=origin message). Each card keeps the
 * category shown here. Unknown is Unsorted. A heading is not a card.
 *
 * Props:
 * - storyId: the story whose Bible receives the cards (required)
 * - messageContent: the message text to segment
 * - messageId: origin message id, stamped as provenance (source_ref)
 * - onClose(): close without adding
 * - onSaved(count): called after a successful add, with the number of cards added
 */
const AddToBibleModal = ({ storyId, messageContent, messageId, onClose, onSaved }) => {
  const { ask, dialog } = usePaidConfirm();
  const [allowed, setAllowed] = useState(false);
  const [cards, setCards] = useState([]);
  const [isSegmenting, setIsSegmenting] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await ask('segment_message', {
        title: ADD_TO_BOOK_NOTES,
        inputTokens: segmentMessageInputTokens(messageContent),
      });
      if (cancelled) return;
      if (!ok) {
        onClose?.();
        return;
      }
      setAllowed(true);
    })();
    return () => { cancelled = true; };
    // Ask once, when the sheet opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Segment the message into proposed cards after the writer confirms.
  // If segmentation yields nothing, fall back to a single editable card
  // pre-filled with the message so the writer can still add it by hand.
  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    const fallbackCard = () => ({
      key: nextKey(),
      name: '',
      description: (messageContent || '').trim(),
      category: UNSORTED_NOTE_CATEGORY,
    });
    (async () => {
      try {
        const res = await storyBibleApi.segmentMessage(storyId, messageContent || '');
        if (cancelled) return;
        const proposed = (res?.cards || [])
          .filter((c) => !isSectionHeading(c?.name))
          .map((c) => {
            const category = categoryForAddedNote(c.category);
            return {
              key: nextKey(),
              name: c.name || '',
              description: c.description || '',
              category: NOTE_CATEGORIES.includes(category) ? category : UNSORTED_NOTE_CATEGORY,
            };
          });
        setCards(proposed.length > 0 ? proposed : [fallbackCard()]);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to segment message:', err);
        setCards([fallbackCard()]);
      } finally {
        if (!cancelled) setIsSegmenting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, storyId, messageContent]);

  const updateCard = (key, field, value) => {
    setCards((prev) => prev.map((c) => (c.key === key ? { ...c, [field]: value } : c)));
  };

  const discardCard = (key) => {
    setCards((prev) => prev.filter((c) => c.key !== key));
  };

  // Split = duplicate the card in place so the writer can trim each half into a
  // distinct idea (one idea per card).
  const splitCard = (key) => {
    setCards((prev) => {
      const idx = prev.findIndex((c) => c.key === key);
      if (idx === -1) return prev;
      const copy = { ...prev[idx], key: nextKey(), category: categoryForAddedNote(prev[idx].category) };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  };

  const addBlank = () => {
    setCards((prev) => [...prev, {
      key: nextKey(), name: '', description: '', category: UNSORTED_NOTE_CATEGORY,
    }]);
  };

  const handleSave = async () => {
    const payload = cards
      .filter((c) => !isSectionHeading(c.name))
      .map((c) => ({
        name: c.name.trim() || null,
        description: c.description.trim(),
        category: categoryForAddedNote(c.category),
      }))
      .filter((c) => c.description);

    if (payload.length === 0) {
      setError('Add at least one note with a description, or cancel.');
      return;
    }

    try {
      setError('');
      setIsSaving(true);
      await storyBibleApi.addCards(storyId, payload, messageId || null);
      if (onSaved) onSaved(payload.length);
      onClose();
    } catch (err) {
      console.error('Failed to add cards to Bible:', err);
      setError('Could not add these to Book Notes. Please try again.');
      setIsSaving(false);
    }
  };

  if (!allowed) return dialog;

  return (
    <div className="add-to-bible-backdrop" onClick={onClose}>
      <div
        className="add-to-bible-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={ADD_TO_BOOK_NOTES}
      >
        <div className="add-to-bible-header">
          <div>
            <h3>{ADD_TO_BOOK_NOTES}</h3>
            <p className="add-to-bible-subtitle">
              Review the notes below. Each is editable — split, discard, or add one.
              Approving saves them on the Notes page in the category shown.
            </p>
          </div>
          <button
            className="add-to-bible-close"
            onClick={onClose}
            aria-label="Close"
            disabled={isSaving}
          >
            ×
          </button>
        </div>

        <div className="add-to-bible-content">
          {isSegmenting ? (
            <div className="add-to-bible-loading">Segmenting into notes…</div>
          ) : (
            <>
              {cards.length === 0 && (
                <p className="add-to-bible-empty">
                  No notes. Add one below, or cancel.
                </p>
              )}
              {cards.map((card) => (
                <div className="add-to-bible-card" key={card.key}>
                  <div className="add-to-bible-card-actions">
                    <button
                      type="button"
                      className="add-to-bible-card-btn"
                      onClick={() => splitCard(card.key)}
                      disabled={isSaving}
                      title="Split into two notes"
                    >
                      Split
                    </button>
                    <button
                      type="button"
                      className="add-to-bible-card-btn add-to-bible-card-btn--danger"
                      onClick={() => discardCard(card.key)}
                      disabled={isSaving}
                      title="Discard this note"
                    >
                      Discard
                    </button>
                  </div>
                  <select
                    className="add-to-bible-card-name"
                    aria-label="Category"
                    value={card.category || UNSORTED_NOTE_CATEGORY}
                    onChange={(e) => updateCard(card.key, 'category', e.target.value)}
                    disabled={isSaving}
                  >
                    {NOTE_CATEGORIES.map((category) => (
                      <option key={category} value={category}>{categoryDisplayName(category)}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="add-to-bible-card-name"
                    value={card.name}
                    onChange={(e) => updateCard(card.key, 'name', e.target.value)}
                    placeholder="Name (optional) — e.g., The Iron Compact"
                    maxLength={80}
                    disabled={isSaving}
                  />
                  <textarea
                    className="add-to-bible-card-desc"
                    value={card.description}
                    onChange={(e) => updateCard(card.key, 'description', e.target.value)}
                    rows={3}
                    placeholder="What this note establishes…"
                    disabled={isSaving}
                  />
                </div>
              ))}

              <button
                type="button"
                className="add-to-bible-add-card"
                onClick={addBlank}
                disabled={isSaving}
              >
                {ADD_A_NOTE}
              </button>

              {error && <span className="add-to-bible-error">{error}</span>}
            </>
          )}
        </div>

        <div className="add-to-bible-footer">
          <button
            type="button"
            className="add-to-bible-cancel-btn"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="add-to-bible-submit-btn"
            onClick={handleSave}
            disabled={isSaving || isSegmenting || cards.length === 0}
          >
            {isSaving ? 'Adding…' : ADD_TO_BOOK_NOTES}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToBibleModal;
